/**
 * Coalesces pending calls and settles their promises from one invocation using the latest
 * arguments.
 *
 * Postconditions: Calls before the timer fires share the inner result or rejection. Calls
 * arriving while inner runs form a later batch; in-flight work is not cancelled.
 *
 * Assumptions: The inner function returns a promise; the delay follows setTimeout semantics. No
 * cancellation API is exposed.
 */
const asyncDebounce = <T extends (...args: any[]) => Promise<any>>(inner: T, ms: number = 0) => {
    type Result = Awaited<ReturnType<T>>;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pending: {
        resolve: (value: Result) => void;
        reject: (reason: unknown) => void;
    }[] = [];

    return (...args: Parameters<T>): Promise<Result> => {
        clearTimeout(timer);

        timer = setTimeout(
            async () => {
                // Calls arriving while inner runs belong to the next batch.
                const batch = pending;
                pending = [];

                try {
                    const result = await inner(...args);
                    batch.forEach(({ resolve }) => resolve(result));
                } catch (error) {
                    batch.forEach(({ reject }) => reject(error));
                }
            },
            ms
        );

        return new Promise<Result>((resolve, reject) => pending.push({ resolve, reject }));
    };
};

export default asyncDebounce;
